import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import type { PlanReviewOrdinanceCompliance, PlanReviewOrdinanceComplianceItem } from "@/api/plan_review";
import { formatElapsedTime, humanizeOrdinanceOutcome, ordinanceOutcomePillClass } from "@/components/plan_review/planReviewDisplay";
import { BookOpen, CheckCircle2, GitCompare, ListChecks, Loader2, Scale } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const STAGES: Array<{ key: string; label: string; description: string; icon: typeof BookOpen }> = [
  { key: "library", label: "Municipal code", description: "Connect to jurisdiction knowledge base", icon: BookOpen },
  { key: "rulepack", label: "Structured rules", description: "Compile or reuse RulePack for this checklist", icon: Scale },
  { key: "compare", label: "Match plan to code", description: "Retrieve sources and compare each topic", icon: GitCompare },
  { key: "record", label: "Record findings", description: "Lock in compliance outcomes", icon: ListChecks },
];

function activeStageIndex(phase: string | undefined): number {
  if (!phase) return 0;
  if (phase === "loading_rule_pack") return 0;
  if (phase === "reusing_rule_pack" || phase === "compiling_rule_pack" || phase === "rule_pack_unavailable") return 1;
  if (phase === "starting" || phase === "researching") return 2;
  if (phase === "adjudicating") return 3;
  return 0;
}

function phaseHeadline(phase: string | undefined, progress: PlanReviewOrdinanceCompliance["progress"]): string {
  const topic = progress?.current_topic?.trim();
  switch (phase) {
    case "loading_rule_pack":
      return "Connecting to your municipality’s code library…";
    case "reusing_rule_pack":
      return "Reusing cached structured rules for faster comparisons.";
    case "compiling_rule_pack":
      return topic
        ? `Compiling structured rule: ${topic}`
        : "Compiling structured rules from ordinance sources…";
    case "rule_pack_unavailable":
      return "Structured RulePack unavailable — using live ordinance retrieval instead.";
    case "starting":
      return "Starting compliance checks…";
    case "researching":
      return topic ? `Retrieving code for: ${topic}` : "Retrieving ordinance sources…";
    case "adjudicating":
      return topic ? `Judging compliance: ${topic}` : "Judging compliance…";
    default:
      return "Preparing ordinance compliance…";
  }
}

function phaseDetail(phase: string | undefined, progress: PlanReviewOrdinanceCompliance["progress"]): string | null {
  if (!progress) return null;
  const key = progress.current_checklist_key?.trim();
  if (phase === "compiling_rule_pack" && key) return `Checklist key: ${key}`;
  if (progress.cache_hit === true && phase === "reusing_rule_pack") return "Cache hit — no full recompile needed.";
  if (typeof progress.rules_compiled_so_far === "number" && phase === "reusing_rule_pack") {
    return `${progress.rules_compiled_so_far} structured rule${progress.rules_compiled_so_far === 1 ? "" : "s"} ready.`;
  }
  if (phase === "rule_pack_unavailable" && progress.rule_pack_error) return progress.rule_pack_error;
  return null;
}

function progressPercent(phase: string | undefined, completed: number, total: number): number {
  if (total <= 0) return 0;
  if (phase === "compiling_rule_pack" || phase === "starting" || phase === "researching") {
    const c = Math.min(completed + 1, total);
    return Math.min(100, Math.round((c / total) * 100));
  }
  return Math.min(100, Math.round((completed / total) * 100));
}

function displayStepIndex(phase: string | undefined, completed: number, total: number): number {
  if (total <= 0) return 0;
  if (phase === "adjudicating") return Math.min(completed, total);
  return Math.min(completed + 1, total);
}

type PlanReviewOrdinanceProgressPanelProps = {
  compliance: PlanReviewOrdinanceCompliance;
};

export function PlanReviewOrdinanceProgressPanel({ compliance }: PlanReviewOrdinanceProgressPanelProps) {
  const progress = compliance.progress;
  const phase = progress?.phase;
  const activeIdx = activeStageIndex(typeof phase === "string" ? phase : undefined);
  const startedAt = compliance.started_at || compliance.requested_at;
  const [elapsedSec, setElapsedSec] = useState(0);

  useEffect(() => {
    if (!startedAt) return;
    const startMs = new Date(startedAt).getTime();
    const tick = () => setElapsedSec(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [startedAt]);

  const completed = progress?.completed ?? 0;
  const total = progress?.total ?? 0;
  const pct = progressPercent(typeof phase === "string" ? phase : undefined, completed, total);
  const stepLabel = displayStepIndex(typeof phase === "string" ? phase : undefined, completed, total);

  const recentItems = useMemo(() => {
    const items: PlanReviewOrdinanceComplianceItem[] = Array.isArray(compliance.items) ? compliance.items : [];
    return items.slice(-6).reverse();
  }, [compliance.items]);

  const chips = useMemo(() => {
    const list: Array<{ label: string; value: string }> = [];
    if (progress?.zoning_key) list.push({ label: "Zoning key", value: progress.zoning_key });
    if (typeof progress?.code_year === "number") list.push({ label: "Code year", value: String(progress.code_year) });
    if (progress?.cache_hit === true) list.push({ label: "RulePack", value: "Cached" });
    if (progress?.cache_hit === false && phase === "compiling_rule_pack") list.push({ label: "RulePack", value: "Compiling" });
    if (typeof progress?.rule_pack_id === "number") list.push({ label: "Pack id", value: String(progress.rule_pack_id) });
    return list;
  }, [progress, phase]);

  return (
    <Card className="border-blue-200 bg-gradient-to-b from-blue-50/90 to-white p-4 text-sm text-slate-800 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-blue-100/80 pb-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-800">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-blue-950">Live ordinance compliance</div>
            <div className="mt-0.5 text-xs text-blue-900/90">{phaseHeadline(typeof phase === "string" ? phase : undefined, progress)}</div>
            {phaseDetail(typeof phase === "string" ? phase : undefined, progress) ? (
              <div className="mt-1 text-[11px] text-blue-800/90">{phaseDetail(typeof phase === "string" ? phase : undefined, progress)}</div>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-medium text-blue-900 shadow-sm ring-1 ring-blue-100">
            {formatElapsedTime(elapsedSec)} elapsed
          </span>
          {startedAt && compliance.last_heartbeat_at ? (
            <span className="text-[10px] text-blue-700/80" title={compliance.last_heartbeat_at}>
              Last update: {new Date(compliance.last_heartbeat_at).toLocaleTimeString()}
            </span>
          ) : null}
        </div>
      </div>

      <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {STAGES.map((stage, i) => {
          const Icon = stage.icon;
          const done = i < activeIdx;
          const active = i === activeIdx;
          return (
            <div
              key={stage.key}
              className={`rounded-lg border px-3 py-2 transition-colors ${
                active
                  ? "border-blue-300 bg-white shadow-sm ring-1 ring-blue-200"
                  : done
                    ? "border-emerald-200 bg-emerald-50/50"
                    : "border-slate-200/80 bg-slate-50/40"
              }`}
            >
              <div className="flex items-center gap-2">
                {done ? (
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
                ) : active ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blue-600" aria-hidden />
                ) : (
                  <Icon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                )}
                <span className={`text-xs font-semibold ${active ? "text-blue-950" : done ? "text-emerald-900" : "text-slate-600"}`}>
                  {stage.label}
                </span>
              </div>
              <p className="mt-1 pl-6 text-[10px] leading-snug text-slate-600">{stage.description}</p>
            </div>
          );
        })}
      </div>

      {total > 0 ? (
        <div className="mt-4 space-y-1">
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-xs text-blue-900">
            <span>
              Step {stepLabel} of {total}
              {progress?.current_topic ? ` — ${progress.current_topic}` : ""}
            </span>
            <span className="font-medium">{pct}%</span>
          </div>
          <Progress value={pct} className="h-2 bg-blue-100" />
        </div>
      ) : (
        <div className="mt-4 text-xs text-blue-900/90">Initializing compliance run…</div>
      )}

      {compliance.summary ? (
        <div className="mt-4 flex flex-wrap gap-2 text-[11px] text-blue-950">
          <span className="rounded-full bg-white/90 px-2 py-0.5 font-medium shadow-sm ring-1 ring-blue-100">
            Compliant: {compliance.summary.compliant ?? 0}
          </span>
          <span className="rounded-full bg-white/90 px-2 py-0.5 font-medium shadow-sm ring-1 ring-blue-100">
            Non-compliant: {compliance.summary.non_compliant ?? 0}
          </span>
          <span className="rounded-full bg-white/90 px-2 py-0.5 font-medium shadow-sm ring-1 ring-blue-100">
            Needs review: {compliance.summary.needs_review ?? 0}
          </span>
        </div>
      ) : null}

      {chips.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {chips.map((c) => (
            <span key={c.label} className="rounded-md border border-blue-100 bg-white/80 px-2 py-0.5 text-[10px] text-blue-900">
              <span className="font-medium text-blue-800">{c.label}:</span> {c.value}
            </span>
          ))}
        </div>
      ) : null}

      {recentItems.length > 0 ? (
        <div className="mt-4 border-t border-blue-100/80 pt-3">
          <div className="text-xs font-semibold text-slate-800">Recent activity</div>
          <ul className="ordinal-scrollbar mt-2 max-h-40 space-y-2 overflow-y-auto pr-1" aria-label="Recent ordinance compliance items">
            {recentItems.map((row) => (
              <li key={row.id} className="flex items-start justify-between gap-2 rounded-md border border-slate-100 bg-white/70 px-2 py-1.5 text-[11px]">
                <span className="min-w-0 flex-1 text-slate-800">{row.topic}</span>
                <span
                  className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${ordinanceOutcomePillClass(row.outcome)}`}
                >
                  {humanizeOrdinanceOutcome(row.outcome)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Card>
  );
}
